import { StandardEvents, CartDiscountUpdateEvent } from '@shopify/events';

const root = () => window.Shopify?.routes?.root || '/';
const api = async (url, options = {}) => {
  const response = await fetch(url, { credentials: 'same-origin', ...options, signal: AbortSignal.timeout(20000) });
  if (!response.ok) throw new Error('Não foi possível consultar o serviço.');
  return response.json();
};
const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));
function formatDeliveryDays(days, fallback) {
  if (!days.length) return fallback;
  const minimum = Math.min(...days), maximum = Math.max(...days);
  if (minimum === maximum) return `${minimum} ${minimum === 1 ? 'dia útil' : 'dias úteis'}`;
  return `${minimum} a ${maximum} dias úteis`;
}

function hoverPhoto(target) {
  if (!(target instanceof Element) || !window.matchMedia?.('(hover: hover) and (pointer: fine)').matches) return null;
  const container = target.closest('.product-media-container--image');
  const gallery = container?.closest('media-gallery[data-ninelle-hover-zoom="true"]');
  if (!gallery || container.closest('dialog, .dialog-zoomed-gallery')) return null;
  const image = container.querySelector('.product-media__image');
  return image ? { container, gallery, image } : null;
}
document.addEventListener('pointermove', event => {
  const photo = hoverPhoto(event.target);
  if (!photo) return;
  const bounds = photo.container.getBoundingClientRect();
  if (!bounds.width || !bounds.height) return;
  const x = Math.max(0, Math.min(100, (event.clientX - bounds.left) / bounds.width * 100));
  const y = Math.max(0, Math.min(100, (event.clientY - bounds.top) / bounds.height * 100));
  photo.container.classList.add('ninelle-photo-hover');
  photo.image.style.setProperty('--ninelle-hover-scale', photo.gallery.dataset.ninelleHoverScale || '2');
  photo.image.style.transformOrigin = `${x}% ${y}%`;
}, { passive: true });
document.addEventListener('pointerout', event => {
  const photo = hoverPhoto(event.target);
  if (photo && !photo.container.contains(event.relatedTarget)) {
    photo.container.classList.remove('ninelle-photo-hover');
    photo.image.style.removeProperty('transform-origin');
  }
}, { passive: true });
document.addEventListener('click', event => {
  if (event.detail > 0 && hoverPhoto(event.target)) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }
}, true);

const cartCookie = () => document.cookie.split('; ').find(part => part.startsWith('cart='))?.slice(5) || '';
const setCartCookie = value => {
  document.cookie = `cart=${value}; path=/; max-age=${value ? 1209600 : 0}; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`;
};
let quoteQueue = Promise.resolve();
const serializeQuote = action => {
  const pending = quoteQueue.then(action, action);
  quoteQueue = pending.catch(() => {});
  return pending;
};
// Adapted from MagazineLuiza's isolated-cart estimator. Always restore the original cookie.
async function isolatedProductQuote(component, params) {
  return serializeQuote(async () => {
    const originalCart = await api(root() + 'cart.js');
    const originalCookie = cartCookie();
    if (!originalCookie) throw new Error('O navegador não disponibilizou a sessão do carrinho. Recarregue a página e tente novamente.');
    const restore = () => setCartCookie(originalCookie);
    const stopPurchase = event => {
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('form[action*="/cart/"], form[action*="/checkout"], [name="checkout"], product-form-component, shopify-accelerated-checkout, cart-items-component, cart-drawer-component, a[href*="/checkout"]')) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    window.addEventListener('pagehide', restore);
    document.addEventListener('click', stopPurchase, true);
    document.addEventListener('submit', stopPurchase, true);
    try {
      const bytes = crypto.getRandomValues(new Uint8Array(16));
      const temporaryCookie = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
      setCartCookie(temporaryCookie);
      if (cartCookie() !== temporaryCookie) throw new Error('O navegador bloqueou a simulação de frete.');
      const temporaryCart = await api(root() + 'cart.js');
      if (temporaryCart.item_count !== 0 || temporaryCart.token === originalCart.token) throw new Error('Não foi possível criar uma simulação separada do carrinho.');
      const form = component.closest('.product-details')?.querySelector('form[action*="/cart/add"]');
      const quantity = Math.max(1, Number(form?.querySelector('[name="quantity"]')?.value) || 1);
      const item = { id: Number(component.dataset.variant), quantity };
      const plan = form?.querySelector('[name="selling_plan"]')?.value;
      if (plan) item.selling_plan = plan;
      if (!Number.isSafeInteger(item.id) || item.id <= 0) throw new Error('Selecione uma opção disponível do produto.');
      await api(root() + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify({ items: [item] }) });
      const simulatedCart = await api(root() + 'cart.js');
      if (simulatedCart.items.length !== 1 || String(simulatedCart.items[0].variant_id) !== component.dataset.variant || simulatedCart.items[0].quantity !== quantity) throw new Error('A simulação do produto não pôde ser validada.');
      return await shippingRates(params);
    } finally {
      restore();
      window.removeEventListener('pagehide', restore);
      document.removeEventListener('click', stopPurchase, true);
      document.removeEventListener('submit', stopPurchase, true);
      if (cartCookie() !== originalCookie) throw new Error('Não foi possível restaurar a sessão do carrinho. Recarregue a página antes de continuar.');
    }
  });
}
async function shippingRates(params) {
  await api(root() + 'cart/prepare_shipping_rates.json?' + params, { method: 'POST' });
  for (let attempt = 0; attempt < 12; attempt++) {
    const response = await api(root() + 'cart/async_shipping_rates.json?' + params);
    if (response !== null && Array.isArray(response.shipping_rates)) return response.shipping_rates;
    await sleep(1000);
  }
  throw new Error('A consulta de frete demorou demais. Tente novamente.');
}

class NinelleVisitors extends HTMLElement {
  connectedCallback() {
    const update = () => {
      const a = Number(this.dataset.min) || 10, b = Number(this.dataset.max) || 20;
      const minimum = Math.min(a, b), maximum = Math.max(a, b);
      const number = Math.floor(Math.random() * (maximum - minimum + 1)) + minimum;
      this.textContent = (this.dataset.message || '').replaceAll('{pessoas}', String(number));
    };
    update();
    clearInterval(this.timer);
    this.timer = setInterval(update, Math.max(5, Number(this.dataset.interval) || 30) * 1000);
  }
  disconnectedCallback() { clearInterval(this.timer); }
}

let locationPromise;
const stateCodes = { acre:'AC', alagoas:'AL', amapa:'AP', amazonas:'AM', bahia:'BA', ceara:'CE', 'distrito federal':'DF', 'espirito santo':'ES', goias:'GO', maranhao:'MA', 'mato grosso':'MT', 'mato grosso do sul':'MS', 'minas gerais':'MG', para:'PA', paraiba:'PB', parana:'PR', pernambuco:'PE', piaui:'PI', 'rio de janeiro':'RJ', 'rio grande do norte':'RN', 'rio grande do sul':'RS', rondonia:'RO', roraima:'RR', 'santa catarina':'SC', 'sao paulo':'SP', sergipe:'SE', tocantins:'TO' };
class NinelleDelivery extends HTMLElement {
  async connectedCallback() {
    try {
      // Approximate location only. The deadlines are explicitly configured by the merchant.
      locationPromise ||= api('https://ipwho.is/?fields=success,country_code,region,city').catch(error => { locationPromise = null; throw error; });
      const location = await locationPromise;
      if (!this.isConnected || !location.success || location.country_code !== 'BR') return;
      const normalizedRegion = (location.region || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      const state = stateCodes[normalizedRegion];
      const rule = this.dataset.rules.split(/\r?\n/).map(line => line.trim().split(':')).find(parts => parts[0]?.toUpperCase() === state);
      if (!rule || rule.length !== 3) return;
      const minimum = Number(rule[1]), maximum = Number(rule[2]);
      if (!Number.isInteger(minimum) || !Number.isInteger(maximum) || minimum < 0 || maximum < minimum) return;
      this.textContent = this.dataset.message.replaceAll('{cidade}', location.city).replaceAll('{min}', String(minimum)).replaceAll('{max}', String(maximum));
      this.hidden = false;
    } catch { this.hidden = true; }
  }
}

class NinelleShipping extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.result = this.querySelector('.ninelle-shipping-result');
    this.messages = JSON.parse(this.querySelector('[data-messages]').textContent);
    this.form.addEventListener('submit', this.submit);
    this.section = this.closest('.shopify-section, dialog');
    this.section?.addEventListener(StandardEvents.productSelect, this.variantChanged);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.cartChanged);
  }
  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.submit);
    this.section?.removeEventListener(StandardEvents.productSelect, this.variantChanged);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.cartChanged);
    this.requestVersion = (this.requestVersion || 0) + 1;
  }
  variantChanged = event => {
    event.promise?.then(({ detail }) => {
      const next = detail?.html?.querySelector(`ninelle-shipping[data-product="${this.dataset.product}"]`);
      if (next) this.dataset.variant = next.dataset.variant;
      this.cartChanged();
    }).catch(() => {});
  };
  cartChanged = () => {
    this.requestVersion = (this.requestVersion || 0) + 1;
    this.result?.replaceChildren();
  };
  submit = async event => {
    event.preventDefault();
    const zip = this.form.elements.cep.value.replace(/\D/g, '');
    if (zip.length !== 8) { this.result.textContent = 'Informe um CEP válido com 8 dígitos.'; return; }
    const version = this.requestVersion = (this.requestVersion || 0) + 1;
    const button = this.form.querySelector('button');
    button.disabled = true;
    this.result.textContent = this.messages.loading;
    try {
      const picker = this.closest('.shopify-section')?.querySelector('variant-picker[data-ninelle-pending="true"]');
      if (this.dataset.product && picker) throw new Error(picker.dataset.ninelleMessage || 'Selecione as opções do produto antes de calcular o frete.');
      const address = await api(`https://viacep.com.br/ws/${zip}/json/`);
      if (address.erro || !address.uf) throw new Error('CEP não encontrado. Confira o CEP informado.');
      let rates;
      const endpoint = this.dataset.endpoint.trim();
      if (endpoint) {
        if (!endpoint.startsWith('/apps/')) throw new Error('Configure uma integração de frete válida nas configurações do tema.');
        const form = this.closest('.product-details')?.querySelector('form[action*="/cart/add"]');
        const quantity = Math.max(1, Number(form?.querySelector('[name="quantity"]')?.value) || 1);
        const response = await api(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ zip, country: 'BR', province: address.uf, variant_id: this.dataset.variant || null, quantity, context: this.dataset.product ? 'product' : 'cart' }) });
        rates = response.shipping_rates;
      } else {
        const params = new URLSearchParams({ 'shipping_address[zip]': zip, 'shipping_address[country]': 'Brazil', 'shipping_address[province]': address.uf });
        if (this.dataset.product) rates = await isolatedProductQuote(this, params);
        else rates = await serializeQuote(async () => {
          const cart = await api(root() + 'cart.js');
          if (!cart.item_count) throw new Error('Adicione produtos ao carrinho para consultar o frete.');
          return shippingRates(params);
        });
      }
      if (version !== this.requestVersion || !this.isConnected) return;
      if (!Array.isArray(rates)) throw new Error(this.messages.error);
      this.result.replaceChildren();
      const heading = document.createElement('p');
      heading.textContent = `${address.localidade} / ${address.uf}`;
      this.result.append(heading);
      if (!rates.length) { this.result.append(document.createTextNode(this.messages.empty)); return; }
      const list = document.createElement('ul');
      for (const rate of rates) {
        const price = Number(rate.price);
        if (!Number.isFinite(price) || price < 0) continue;
        const item = document.createElement('li');
        const days = Array.isArray(rate.delivery_days) ? rate.delivery_days.map(Number).filter(n => Number.isInteger(n) && n >= 0) : [];
        const deadline = formatDeliveryDays(days, this.messages.noDays);
        item.textContent = `${rate.name}: ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: rate.currency || this.messages.currency }).format(price)} · ${deadline}`;
        list.append(item);
      }
      if (!list.children.length) throw new Error(this.messages.error);
      this.result.append(list);
    } catch (error) {
      if (version === this.requestVersion && this.isConnected) this.result.textContent = error.name === 'TimeoutError' || error instanceof TypeError ? this.messages.error : error.message;
    } finally { button.disabled = false; }
  };
}

for (const [name, implementation] of [['ninelle-visitors', NinelleVisitors], ['ninelle-delivery', NinelleDelivery], ['ninelle-shipping', NinelleShipping]]) {
  if (!customElements.get(name)) customElements.define(name, implementation);
}

// A known coupon can be applied through Shopify. Private discount rules cannot be enumerated by a theme.
const couponConfiguration = document.querySelector?.('[data-ninelle-coupon-settings]');
if (couponConfiguration) {
  const configuration = JSON.parse(couponConfiguration.textContent);
  const code = configuration.code.trim();
  let previousSignature = '', suppressed = false, pending = false;
  const status = text => {
    const element = document.querySelector('[data-ninelle-coupon-status]');
    if (element) { element.textContent = text; element.hidden = !text; }
  };
  const apply = async () => {
    if (!code || suppressed || pending) return;
    pending = true;
    try {
      await serializeQuote(async () => {
        const cart = await api(root() + 'cart.js');
        if (!cart.item_count) return;
        const existing = (cart.discount_codes || []).map(discount => discount.code);
        if (existing.some(value => value.toLowerCase() === code.toLowerCase())) return;
        const signature = JSON.stringify([cart.items.map(item => [item.key, item.quantity, item.original_price]), existing]);
        if (signature === previousSignature) return;
        previousSignature = signature;
        const codes = [...existing, code];
        const sections = [...new Set(Array.from(document.querySelectorAll('cart-items-component[data-section-id]'), element => element.dataset.sectionId))].slice(0, 5);
        const deferred = CartDiscountUpdateEvent.createPromise();
        document.dispatchEvent(new CartDiscountUpdateEvent({ discountCodes: codes.map(value => ({ code: value })), promise: deferred.promise, detail: { source: 'ninelle-auto-coupon' } }));
        try {
          const updated = await api(root() + 'cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ discount: codes.join(','), sections, sections_url: location.pathname }) });
          const accepted = updated.discount_codes?.some(discount => discount.code.toLowerCase() === code.toLowerCase() && discount.applicable);
          status(accepted ? configuration.success.replaceAll('{cupom}', code) : configuration.error);
          deferred.resolve({ cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(updated), detail: { sections: updated.sections } });
        } catch (error) { deferred.reject(error); throw error; }
      });
    } catch { status(configuration.error); }
    finally { pending = false; }
  };
  document.addEventListener(StandardEvents.cartLinesUpdate, event => {
    event.promise?.then(apply).catch(() => {});
  });
  document.addEventListener(StandardEvents.cartDiscountUpdate, event => {
    if (event.detail?.source === 'ninelle-auto-coupon') return;
    const requested = event.discountCodes;
    if (Array.isArray(requested) && !requested.some(discount => discount.code.toLowerCase() === code.toLowerCase())) suppressed = true;
  });
  apply();
}

